import type {ChangeEvent} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {useSwitch} from './useSwitch';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * The `ComponentProps` interface defines the properties for the `Switch` component.
 * This interface specifies all the configuration options available for the switch toggle input, including its initial state, validation rules, and event handling.
 * The interface ensures type safety and provides clear documentation for developers using the Switch component in forms or standalone implementations.
 */
interface ComponentProps {
    /**
     * The initial boolean value for the switch state when the component first renders.
     * This determines whether the switch appears in the "on" (true) or "off" (false) position initially.
     * The component's internal state will be initialized with this value and can be changed through user interaction.
     */
    initialValue: boolean;
    /**
     * The name attribute for the underlying checkbox input element used by the switch.
     * This property is essential for form submissions and helps identify the field when processing form data.
     * The name should be unique within the form context to avoid conflicts with other form elements.
     */
    name: string;
    /**
     * Optional callback function that gets invoked when the switch value changes through user interaction.
     * The function receives a ChangeEvent containing the new checked state of the underlying checkbox input.
     * This allows parent components to respond to switch changes and integrate with form validation or state management systems.
     */
    onChange?(e: ChangeEvent<HTMLInputElement>): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Switch'
     */
    testId?: string;
    /**
     * Optional validation function that validates the current switch value and returns error messages or null.
     * The function receives the current boolean value of the switch and should return a string error message if validation fails, or null/undefined if valid.
     * This enables form-level validation integration and provides user feedback for required or conditional switch states.
     */
    validation?: ValidationFunc | undefined;
}

/**
 * The Switch component renders a toggle switch input element that allows users to switch between two states (on/off or true/false).
 * This component uses MobX for reactive state management and provides visual feedback through styled components with smooth transitions.
 * The switch maintains its state internally while also supporting external onChange handlers for form integration and validation.
 *
 * @param props              The component props.
 * @param props.initialValue The initial boolean value for the switch state.
 * @param props.name         The name attribute for the underlying checkbox input element.
 * @param props.onChange     Optional callback function that gets called when the switch value changes.
 * @param props.testId       Test identifier for component testing purposes.
 * @param props.validation   Optional validation function to validate the switch value.
 * @returns The Switch component JSX element wrapped with MobX observer for reactivity.
 *
 * @example
 * ```tsx
 * <Switch
 *     initialValue={false}
 *     name="notifications"
 *     onChange={(e) => console.log('Switch changed:', e.target.checked)}
 *     testId="notification-switch"
 *     validation={(value) => value ? null : 'Must be enabled'}
 * />
 * ```
 */
const Switch = observer(({
    initialValue,
    name,
    onChange = () => {},
    testId = 'Switch',
    validation
}: ComponentProps) => {
    const {fieldRef, handleChange, value} = useSwitch(
        name,
        initialValue,
        validation,
        onChange
    );

    return (
        <Wrapper data-cy={testId}>
            <Checkbox ref={fieldRef} checked={value} name={name} type="checkbox" onChange={handleChange} />
            <SwitchElem />
        </Wrapper>
    );
});

Switch.displayName = 'Switch';

export {Switch};

const Wrapper = styled.label`
    align-items: center;
    cursor: pointer;
    display: inline-flex;
    position: relative;
`;

const Checkbox = styled.input`
    border-width: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;

const SwitchElem = styled.div`
    background-color: ${({theme}) => theme.colors.backgroundNeutral};
    border-radius: 9999px;
    box-sizing: border-box;
    cursor: pointer;
    height: 2.5rem;
    width: 5rem;

    ${Checkbox}:checked + & {
        background-color: ${({theme}) => theme.colors.primary};
    }

    ${Checkbox}:focus-visible + & {
        outline: 2px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 1);
    }

    &:after {
        background-color: rgb(255 255 255 / 1);
        border-radius: 9999px;
        content: '';
        height: 2.1rem;
        left: ${spacing(0.5)};
        position: absolute;
        top: ${spacing(0.5)};
        transition: background-color 0.15s ease-in-out, transform 0.15s cubic-bezier(.4, 0, .2, 1);
        width: 2.1rem;
    }

    ${Checkbox}:checked + &:after {
        transform: translate(2.5rem, 0%);
    }
`;